import { api, get, post, del } from './client.js';
const patch = (path, body) => api('PATCH', path, { body });

/**
 * Outbound Campaign API client（v2 — Firestore-backed）
 * 跟 v1 quick-dial 並存：v1 在 calls.js / OutboundPage（fire-and-forget），v2 持久化 + 排程
 *
 * Backend mount: /api/ivr/admin/tenants/:hostId/campaigns
 */

const base = (hostId) => `/api/ivr/admin/tenants/${encodeURIComponent(hostId)}/campaigns`;

export const campaignApi = {
    list: (hostId, { status, limit } = {}) => {
        const qs = new URLSearchParams();
        if (status) qs.set('status', status);
        if (limit) qs.set('limit', String(limit));
        const q = qs.toString();
        return get(`${base(hostId)}${q ? '?' + q : ''}`);
    },
    get: (hostId, id) => get(`${base(hostId)}/${encodeURIComponent(id)}`),
    create: (hostId, body) => post(base(hostId), body),
    update: (hostId, id, patchBody) =>
        patch(`${base(hostId)}/${encodeURIComponent(id)}`, patchBody),
    remove: (hostId, id) => del(`${base(hostId)}/${encodeURIComponent(id)}`),

    // 狀態
    start: (hostId, id) => post(`${base(hostId)}/${encodeURIComponent(id)}/start`, {}),
    pause: (hostId, id) => post(`${base(hostId)}/${encodeURIComponent(id)}/pause`, {}),
    resume: (hostId, id) => post(`${base(hostId)}/${encodeURIComponent(id)}/resume`, {}),
    finish: (hostId, id) => post(`${base(hostId)}/${encodeURIComponent(id)}/finish`, {}),

    // 把 retry_pending / in_progress / done_failed contacts 全部 reset 成 pending + 清 attempts
    // includeSuccess=true 會連 done_success 一併重撥
    resetRetry: (hostId, id, { includeSuccess = false } = {}) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/contacts/reset-retry${includeSuccess ? '?includeSuccess=1' : ''}`, {}),

    // 立即觸發 scheduler tick — 給「立即撥」用，不用等 60s
    triggerTick: (hostId, id) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/trigger-tick`, {}),

    // 立即撥 / 繼續撥 — 手動撥打：無視重試冷卻、resume 成 running、立刻 tick
    // （自動排程不變，仍尊重冷卻 + 撥打時段；冷卻只是給自動重撥用）
    dialNow: (hostId, id) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/dial-now`, {}),

    // 名單
    importContacts: (hostId, id, { contacts, importBatchId } = {}) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/contacts/import`, { contacts, importBatchId }),
    listContacts: (hostId, id, { status, limit, offset } = {}) => {
        const qs = new URLSearchParams();
        if (status) qs.set('status', status);
        if (limit) qs.set('limit', String(limit));
        if (offset) qs.set('offset', String(offset));
        const q = qs.toString();
        return get(`${base(hostId)}/${encodeURIComponent(id)}/contacts${q ? '?' + q : ''}`);
    },

    // 分類設定（客戶自助）：名單哪一欄當分類 + 各分類值分布 / 開場白覆蓋率；column 可試算換欄
    categoryPreview: (hostId, id, { column } = {}) =>
        get(`${base(hostId)}/${encodeURIComponent(id)}/category-preview${column !== undefined && column !== null ? '?column=' + encodeURIComponent(column) : ''}`),
    // 寫分類欄位：scope 'campaign'（只此活動）| 'avatar'（此 AI Agent 所有活動共用）
    setCategoryColumn: (hostId, id, { column, scope }) =>
        api('PUT', `${base(hostId)}/${encodeURIComponent(id)}/category-column`, { body: { column, scope } }),
    // 動態欄位 schema（給手動新增表單用）
    contactSchema: (hostId, id) =>
        get(`${base(hostId)}/${encodeURIComponent(id)}/contacts/schema`),
    // 手動新增一筆 { variables, phones[], sourceRow? }
    addContact: (hostId, id, body) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/contacts/add`, body),
    // 編輯一筆 { variables?, phones? }
    editContact: (hostId, id, contactId, body) =>
        patch(`${base(hostId)}/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`, body),
    // 刪除一筆
    deleteContact: (hostId, id, contactId) =>
        del(`${base(hostId)}/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`),
    // 批次刪除
    bulkDeleteContacts: (hostId, id, contactIds) =>
        post(`${base(hostId)}/${encodeURIComponent(id)}/contacts/bulk-delete`, { contactIds }),

    // CSV 匯出（名單快照，每筆聯絡人一列，含最後撥打結果）
    // ⚠️ 要用 client.js 的 download() 帶 token 抓，不能用 <a href download>（不帶 token → proxy 401）
    exportCsvUrl: (hostId, id) =>
        `${base(hostId)}/${encodeURIComponent(id)}/contacts/export.csv`,

    // 每日撥打統計 xlsx（每日彙總 + 通話明細兩個工作表）；from/to = YYYY-MM-DD（台北時區，含端點）
    exportDailyXlsxUrl: (hostId, id, { from, to } = {}) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const q = qs.toString();
        return `${base(hostId)}/${encodeURIComponent(id)}/calls/export.xlsx${q ? '?' + q : ''}`;
    },
};
